/**
 * NodeWriteService.js
 *
 * Central place for Firestore node persistence.
 */

// Firebase
import { Timestamp, arrayRemove, arrayUnion, deleteDoc, deleteField, doc, serverTimestamp, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import { getNode } from 'services/nodes/NodeReadService';

// Utilities
import { chunkArray } from 'utilities/Arrays';
import { normalizeNodeForWrite } from 'utilities/NodeSecurity';
import { getResolvedFields } from 'utilities/NodeType';
import { buildSearchIndexFromTexts } from 'services/search/SearchIndexService';
import { buildNodeSearchIndexFromNode } from 'utilities/NodeSearchIndex';
import { buildUserSummary } from 'utilities/Users';

// Constants
const MAX_BATCH_OPS = 450;

// Detects non-empty strings
const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0;

const normalizeTagIndex = (tags) => {
	if (!Array.isArray(tags)) return [];
	return tags
		.map((tag) => (typeof tag === 'string' ? tag.toLowerCase().trim() : ''))
		.filter((tag) => tag.length > 0);
};

const buildFallbackNodeIndex = (payload) => {
	const safeTitle = typeof payload?.title === 'string' ? payload.title : '';
	const tagsIndex = normalizeTagIndex(payload?.tags);
	return buildSearchIndexFromTexts([safeTitle, tagsIndex]);
};

const shouldHydrateCreateIndex = ({ payload, merge }) => {
	if (merge !== false) return false;
	if (!payload || typeof payload !== 'object') return false;
	if (Array.isArray(payload.index) && payload.index.length > 0) return false;
	if (!isNonEmptyString(payload?.type)) return false;
	return true;
};

const shouldRebuildIndexForPatch = (patch) => {
	if (!patch || typeof patch !== 'object') return false;
	if (Object.prototype.hasOwnProperty.call(patch, 'index')) return false;

	const keys = Object.keys(patch);
	if (keys.some((key) => key === 'title' || key === 'tags' || key === 'type' || key === 'article')) return true;
	if (keys.some((key) => key.startsWith('data_'))) return true;

	return false;
};

const normalizeFieldType = (field) => String(field?.type || '').trim().toLowerCase();

const getPeopleFieldDefs = (typeKey) => {
	return getResolvedFields(typeKey, { includeHidden: true })
		.filter((field) => normalizeFieldType(field) === 'people');
};

const getPeopleEntryKey = (entry) => {
	if (typeof entry === 'string' || typeof entry === 'number') {
		return String(entry).trim();
	}

	if (!entry || typeof entry !== 'object') return '';

	return String(entry?.userKey || entry?.key || entry?.data_profile?.key || '').trim();
};

const normalizePeopleKeys = (value) => {
	const list = Array.isArray(value) ? value : value == null ? [] : [value];
	const seen = new Set();

	return list
		.map((entry) => getPeopleEntryKey(entry))
		.filter((entry) => {
			if (!entry || seen.has(entry)) return false;
			seen.add(entry);
			return true;
		})
		.sort((left, right) => left.localeCompare(right));
};

const areStringListsEqual = (left, right) => {
	const normalizedLeft = Array.isArray(left) ? left : [];
	const normalizedRight = Array.isArray(right) ? right : [];

	if (normalizedLeft.length !== normalizedRight.length) return false;

	for (let index = 0; index < normalizedLeft.length; index += 1) {
		if (normalizedLeft[index] !== normalizedRight[index]) return false;
	}

	return true;
};

const buildActorAuditPatch = (actor) => {
	const actorKey = String(
		actor?.key
		|| actor?.userKey
		|| actor?.uid
		|| ''
	).trim();

	if (!actorKey) return {};

	return {
		updatedBy: actorKey,
		updatedByUser: buildUserSummary(actor),
	};
};

const extractFieldValueFromNode = ({ node, field, typeKey }) => {
	const fieldName = String(field?.name || field?.key || '').trim();
	if (!fieldName) return undefined;

	const sourceType = String(field?.source || typeKey || '').trim().toLowerCase();
	const sectionKey = sourceType ? `data_${sourceType}` : '';

	if (sectionKey) {
		const section = node?.[sectionKey];
		if (section && typeof section === 'object' && !Array.isArray(section)) {
			if (Object.prototype.hasOwnProperty.call(section, fieldName)) {
				return section[fieldName];
			}
		}
	}

	const flatKey = `data_${fieldName}`;
	if (Object.prototype.hasOwnProperty.call(node || {}, flatKey)) {
		return node[flatKey];
	}

	if (Object.prototype.hasOwnProperty.call(node || {}, fieldName)) {
		return node[fieldName];
	}

	return undefined;
};

const applyPatchValue = (target, key, value) => {
	if (!key.includes('.')) {
		target[key] = value;
		return;
	}

	const segments = key.split('.').filter(Boolean);
	if (!segments.length) return;

	let cursor = target;
	for (let index = 0; index < segments.length - 1; index += 1) {
		const segment = segments[index];
		const existing = cursor?.[segment];
		if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
			cursor[segment] = {};
		}
		cursor = cursor[segment];
	}

	cursor[segments[segments.length - 1]] = value;
};

const buildNodeSnapshotWithPatch = (node, patch) => {
	const snapshot = {
		...(node && typeof node === 'object' ? node : {}),
	};

	Object.entries(patch || {}).forEach(([key, value]) => {
		applyPatchValue(snapshot, key, value);
	});

	return snapshot;
};

const buildDerivedPeopleKeys = (node) => {
	const typeKey = String(node?.type || '').trim();
	if (!typeKey) return null;

	const peopleFieldDefs = getPeopleFieldDefs(typeKey);
	if (!peopleFieldDefs.length) {
		return Array.isArray(node?.peopleKeys) && node.peopleKeys.length ? [] : null;
	}

	const seen = new Set();
	const peopleKeys = [];

	peopleFieldDefs.forEach((field) => {
		const rawValue = extractFieldValueFromNode({ node, field, typeKey });
		normalizePeopleKeys(rawValue).forEach((entry) => {
			if (!entry || seen.has(entry)) return;
			seen.add(entry);
			peopleKeys.push(entry);
		});
	});

	return peopleKeys.sort((left, right) => left.localeCompare(right));
};

const withDerivedCreateIndex = (payload, merge) => {
	if (!shouldHydrateCreateIndex({ payload, merge })) return payload;

	let index = buildFallbackNodeIndex(payload);

	try {
		index = buildNodeSearchIndexFromNode(payload);
	} catch (error) {
		console.warn('[NodeWriteService] createNode index hydrate fallback used:', error);
	}

	return {
		...payload,
		index,
	};
};

const withDerivedCreatePeopleKeys = (payload) => {
	if (!payload || typeof payload !== 'object') return payload;

	const peopleKeys = buildDerivedPeopleKeys(payload);
	if (peopleKeys === null) return payload;

	return {
		...payload,
		peopleKeys,
	};
};


/**
 * Convenience wrapper so UI/components don't import firebase/firestore directly.
 */
export function nowTimestamp() {
	return Timestamp.now();
}

/**
 * Creates a new node document in Firestore.
 * 
 * @param {string} nodeKey - The key for the new node.
 * @param {Object} data - The data payload for the new node.
 * 
 * @returns {Promise<void>} Resolves when the node is created.
 */
export async function createNode(nodeKey, data) {

	// Validate inputs
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('createNode: nodeKey is required');
	}
	if (!data || typeof data !== 'object') {
		throw new Error('createNode: data object is required');
	}

	// Prepare payload with timestamps
	const payload = {
		...data,
		key: nodeKey,
		dateCreated: serverTimestamp(),
		dateModified: serverTimestamp(),
	};

	// Write document
	await setNodeDoc(nodeKey, payload, { merge: false });
}

/**
 * Updates an existing node document in Firestore.
 * 
 * @param {string} nodeKey - The key of the node to update.
 * @param {Object} updates - The fields to update.
 * 
 * @param {Object} [options] - Optional write options.
 * @param {object|null} [options.actor=null] - The user performing the update.
 *
 * @returns {Promise<void>} Resolves when the node is updated.
 */
export async function updateNode(nodeKey, updates, options = {}) {

	// Validate inputs
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('updateNode: nodeKey is required');
	}
	if (!updates || typeof updates !== 'object') {
		throw new Error('updateNode: updates object is required');
	}

	const patch = {
		...updates,
		...buildActorAuditPatch(options?.actor || null),
		dateModified: serverTimestamp(),
	};

	let currentNode = null;
	let mergedNode = null;

	try {
		currentNode = await getNode(nodeKey);
		if (currentNode) {
			mergedNode = buildNodeSnapshotWithPatch(currentNode, updates);
		}
	} catch (error) {
		console.warn('[NodeWriteService] updateNode current node load failed:', error);
	}

	if (shouldRebuildIndexForPatch(updates)) {
		try {
			if (mergedNode) {
				patch.index = buildNodeSearchIndexFromNode(mergedNode);
			}
		} catch (error) {
			console.warn('[NodeWriteService] updateNode index rebuild failed:', error);
		}
	}

	if (mergedNode) {
		const derivedPeopleKeys = buildDerivedPeopleKeys(mergedNode);
		if (derivedPeopleKeys !== null) {
			const currentPeopleKeys = normalizePeopleKeys(currentNode?.peopleKeys);
			if (!areStringListsEqual(currentPeopleKeys, derivedPeopleKeys)) {
				patch.peopleKeys = derivedPeopleKeys;
			}
		}
	}

	await patchNode(nodeKey, patch);
}

/**
 * Convenience wrappers so UI/components don't import firebase/firestore directly.
 */
export function arrayUnionValue(...values) {
	return arrayUnion(...values);
}
export function arrayRemoveValue(...values) {
	return arrayRemove(...values);
}
export function deleteFieldValue() {
	return deleteField();
}

const buildTrashPatch = ({ trashedByKey = null, extraFields = {} } = {}) => ({
	...extraFields,
	dateModified: serverTimestamp(),
	isTrashed: true,
	trashedAt: serverTimestamp(),
	trashedByKey: isNonEmptyString(trashedByKey) ? trashedByKey : null,
});

const buildRestorePatch = ({ extraFields = {} } = {}) => ({
	...extraFields,
	dateModified: serverTimestamp(),
	isTrashed: false,
	trashedAt: null,
	trashedByKey: null,
});

/**
 * Writes a node document without normalization.
 * Use for strict callers that have already normalized/constructed the exact payload.
 * 
 * @param {string} nodeKey - The node document key.
 * @param {Object} payload - The exact payload to write.
 * @param {Object} options - Options: { merge }.
 * 
 * @returns {Promise<void>} Resolves when the node is written.
 */
export async function setNodeDoc(nodeKey, payload, options = {}) {

	// Options
	const { merge = true } = options;

	// Validate inputs
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('setNodeDoc: nodeKey is required');
	}
	if (!payload || typeof payload !== 'object') {
		throw new Error('setNodeDoc: payload object is required');
	}

	// Enforce invariant: stored key must equal document id.
	if (payload?.key && payload.key !== nodeKey) {
		console.warn(`[NodeWriteService] setNodeDoc key mismatch; overriding payload.key (${payload.key}) -> ${nodeKey}`);
	}
	const finalPayload = {
		...withDerivedCreatePeopleKeys(withDerivedCreateIndex(payload, merge)),
		key: nodeKey,
	};

	// Write document
	await setDoc(doc(db, collections.nodes, nodeKey), finalPayload, { merge });
}

/**
 * Writes the secured payload into `_nodes/{nodeKey}/secured/{docId}`.
 * 
 * @param {string} nodeKey - The node document key.
 * @param {Object} payload - The exact secured payload to write.
 * @param {Object} options - Options: { merge, docId }.
 */
export async function setNodeSecuredDoc(nodeKey, payload, options = {}) {

	// Options
	const { merge = true, docId = 'main' } = options;

	// Validate inputs
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('setNodeSecuredDoc: nodeKey is required');
	}
	if (!isNonEmptyString(docId)) {
		throw new Error('setNodeSecuredDoc: docId is required');
	}
	if (!payload || typeof payload !== 'object') {
		throw new Error('setNodeSecuredDoc: payload object is required');
	}

	// Write document
	await setDoc(doc(db, collections.nodes, nodeKey, 'secured', docId), payload, { merge });
}

/**
 * Batch writes multiple node documents without normalization.
 * Each entry must be `{ key, payload }`.
 * 
 * @param {Array} entries - Array of entries to write.
 * @param {Object} options - Options: { merge }.
 */
export async function setNodeDocs(entries, options = {}) {

	// Options
	const { merge = false } = options;

	// Validate inputs
	if (!Array.isArray(entries) || entries.length === 0) return;

	// Normalize entries
	const normalized = entries
		.filter((entry) => isNonEmptyString(entry?.key) && entry?.payload && typeof entry.payload === 'object')
		.map((entry) => ({ key: entry.key, payload: entry.payload }));

	// Skip if nothing valid
	if (normalized.length === 0) return;

	// Chunk and batch write
	const chunks = chunkArray(normalized, MAX_BATCH_OPS);

	// Process each chunk
	for (const chunk of chunks) {

		// Create batch
		const batch = writeBatch(db);

		// Add each entry to batch
		chunk.forEach(({ key, payload }) => {
			if (payload?.key && payload.key !== key) {
				console.warn(`[NodeWriteService] setNodeDocs key mismatch; overriding payload.key (${payload.key}) -> ${key}`);
			}
			batch.set(doc(db, collections.nodes, key), {
				...withDerivedCreatePeopleKeys(withDerivedCreateIndex(payload, merge)),
				key,
			}, { merge });
		});

		// Commit batch
		await batch.commit();
	}
}

/**
 * Batch writes multiple secured payload docs at `_nodes/{key}/secured/{docId}`.
 * Each entry must be `{ key, payload }`.
 * 
 * @param {Array} entries - Array of entries to write.
 * @param {Object} options - Options: { merge, docId }.
 */
export async function setNodeSecuredDocs(entries, options = {}) {

	// Options
	const { merge = true, docId = 'main' } = options;

	// Validate inputs
	if (!Array.isArray(entries) || entries.length === 0) return;

	// Validate docId
	if (!isNonEmptyString(docId)) {
		throw new Error('setNodeSecuredDocs: docId is required');
	}

	// Normalize entries
	const normalized = entries
		.filter((entry) => isNonEmptyString(entry?.key) && entry?.payload && typeof entry.payload === 'object')
		.map((entry) => ({ key: entry.key, payload: entry.payload }));

	// Skip if nothing valid
	if (normalized.length === 0) return;

	// Chunk and batch write
	const chunks = chunkArray(normalized, MAX_BATCH_OPS);

	// Process each chunk
	for (const chunk of chunks) {

		// Create batch
		const batch = writeBatch(db);

		// Add each entry to batch
		chunk.forEach(({ key, payload }) => {
			batch.set(doc(db, collections.nodes, key, 'secured', docId), payload, { merge });
		});

		// Commit batch
		await batch.commit();
	}
}

/**
 * Upserts a node document.
 *
 * - Requires `node.key`.
 * - Normalizes via `normalizeNodeForWrite`.
 * - Defaults to merge=true (safer for incremental writes).
 * 
 * @param {Object} node - The node object to upsert.
 * @param {Object} options - Options: { merge }.
 */
export async function upsertNode(node, options = {}) {

	// Options
	const { merge = true } = options;

	// Validate input
	const key = node?.key;

	// Validate key
	if (!isNonEmptyString(key)) {
		throw new Error('upsertNode: node.key is required');
	}

	// Write document
	const ref = doc(db, collections.nodes, key);

	// Write normalized node
	const normalized = normalizeNodeForWrite(node);
	if (normalized?.key && normalized.key !== key) {
		console.warn(`[NodeWriteService] upsertNode key mismatch; overriding payload.key (${normalized.key}) -> ${key}`);
	}
	await setDoc(ref, {
		...withDerivedCreatePeopleKeys(normalized),
		key,
	}, { merge });

	// Return the node
	return node;
}

/**
 * Deletes a node document.
 * 
 * @param {string} nodeKey - The key of the node to delete.
 */
export async function deleteNode(nodeKey) {

	// Validate input
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('deleteNode: nodeKey is required');
	}

	// Delete document
	await deleteDoc(doc(db, collections.nodes, nodeKey));
}

/**
 * Marks a node as trashed without removing the document.
 *
 * @param {string} nodeKey - The key of the node to trash.
 * @param {{ trashedByKey?: string|null, extraFields?: Object }} options
 */
export async function moveNodeToTrash(nodeKey, options = {}) {
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('moveNodeToTrash: nodeKey is required');
	}

	await patchNode(nodeKey, buildTrashPatch(options));
}

/**
 * Clears trash metadata and restores a node to active state.
 *
 * @param {string} nodeKey - The key of the node to restore.
 * @param {{ extraFields?: Object }} options
 */
export async function restoreNode(nodeKey, options = {}) {
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('restoreNode: nodeKey is required');
	}

	await patchNode(nodeKey, buildRestorePatch(options));
}

/**
 * Batch upserts multiple nodes.
 *
 * - Requires each node to have a non-empty `key`.
 * - Normalizes via `normalizeNodeForWrite`.
 * 
 * @param {Array} nodes - Array of node objects to upsert.
 * @param {Object} options - Options: { merge }.
 */
export async function upsertNodes(nodes, options = {}) {

	// Options
	const { merge = true } = options;

	// Validate input
	if (!Array.isArray(nodes) || nodes.length === 0) return;

	// Chunk and batch write
	const chunks = chunkArray(nodes, MAX_BATCH_OPS);

	// Process each chunk
	for (const chunk of chunks) {

		// Create batch
		const batch = writeBatch(db);

		// Add each node to batch
		chunk.forEach((node) => {

			// Validate key
			const key = node?.key;
			if (!isNonEmptyString(key)) {
				throw new Error('upsertNodes: every node must have a non-empty key');
			}

			// Add to batch
			const normalized = normalizeNodeForWrite(node);
			if (normalized?.key && normalized.key !== key) {
				console.warn(`[NodeWriteService] upsertNodes key mismatch; overriding payload.key (${normalized.key}) -> ${key}`);
			}
			batch.set(doc(db, collections.nodes, key), {
				...withDerivedCreatePeopleKeys(normalized),
				key,
			}, { merge });
		});

		// Commit batch
		await batch.commit();
	}
}

/**
 * Batch deletes multiple nodes.
 * 
 * @param {Array} nodeKeys - Array of node keys to delete.
 */
export async function deleteNodes(nodeKeys) {

	// Validate input
	if (!Array.isArray(nodeKeys) || nodeKeys.length === 0) return;

	// Filter valid keys
	const keys = nodeKeys.filter(isNonEmptyString);

	// Skip if nothing valid
	if (keys.length === 0) return;

	// Chunk and batch delete
	const chunks = chunkArray(keys, MAX_BATCH_OPS);

	// Process each chunk
	for (const chunk of chunks) {

		// Create batch
		const batch = writeBatch(db);

		// Add each delete to batch
		chunk.forEach((key) => {
			batch.delete(doc(db, collections.nodes, key));
		});

		// Commit batch
		await batch.commit();
	}
}

/**
 * Batch marks multiple nodes as trashed.
 *
 * @param {Array} nodeKeys - Array of node keys to trash.
 * @param {{ trashedByKey?: string|null, extraFields?: Object }} options
 */
export async function moveNodesToTrash(nodeKeys, options = {}) {
	if (!Array.isArray(nodeKeys) || nodeKeys.length === 0) return;

	const keys = nodeKeys.filter(isNonEmptyString);
	if (keys.length === 0) return;

	const chunks = chunkArray(keys, MAX_BATCH_OPS);

	for (const chunk of chunks) {
		const batch = writeBatch(db);

		chunk.forEach((key) => {
			batch.update(doc(db, collections.nodes, key), buildTrashPatch(options));
		});

		await batch.commit();
	}
}

/**
 * Batch restores multiple trashed nodes.
 *
 * @param {Array} nodeKeys - Array of node keys to restore.
 * @param {{ extraFields?: Object }} options
 */
export async function restoreNodes(nodeKeys, options = {}) {
	if (!Array.isArray(nodeKeys) || nodeKeys.length === 0) return;

	const keys = nodeKeys.filter(isNonEmptyString);
	if (keys.length === 0) return;

	const chunks = chunkArray(keys, MAX_BATCH_OPS);

	for (const chunk of chunks) {
		const batch = writeBatch(db);

		chunk.forEach((key) => {
			batch.update(doc(db, collections.nodes, key), buildRestorePatch(options));
		});

		await batch.commit();
	}
}

/**
 * Updates a node document with a patch object (no normalization).
 * Use when setting specific fields.
 * 
 * @param {string} nodeKey - The key of the node to update.
 * @param {Object} patch - The patch object with fields to update.
 */
export async function patchNode(nodeKey, patch) {

	// Validate inputs
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('patchNode: nodeKey is required');
	}
	if (!patch || typeof patch !== 'object') {
		throw new Error('patchNode: patch object is required');
	}

	// Update document
	await updateDoc(doc(db, collections.nodes, nodeKey), patch);
}
